import React from 'react';
import { Source, Layer } from 'react-map-gl';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Close as CloseIcon, Undo as UndoIcon } from '@mui/icons-material';

export interface MeasurePoint {
  lng: number;
  lat: number;
}

function haversineDistance(a: MeasurePoint, b: MeasurePoint): number {
  const R = 6371e3;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLng * sinLng;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} m`;
  if (meters < 100_000) return `${(meters / 1000).toFixed(2)} km`;
  return `${(meters / 1000).toFixed(1)} km`;
}

export function totalDistance(points: MeasurePoint[]): number {
  let d = 0;
  for (let i = 1; i < points.length; i++) d += haversineDistance(points[i - 1], points[i]);
  return d;
}

export function segmentDistances(points: MeasurePoint[]): number[] {
  return points.slice(1).map((p, i) => haversineDistance(points[i], p));
}

export interface MapMeasureOverlayProps {
  points: MeasurePoint[];
  onUndo: () => void;
  onClear: () => void;
}

const MapMeasureOverlay: React.FC<MapMeasureOverlayProps> = ({ points, onUndo, onClear }) => {
  if (points.length === 0) return null;

  const lineGeoJson: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features:
      points.length >= 2
        ? [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: points.map((p) => [p.lng, p.lat]),
              },
            },
          ]
        : [],
  };

  const pointsGeoJson: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: points.map((p, i) => ({
      type: 'Feature' as const,
      properties: {
        index: i,
        distance: i === 0 ? '0' : formatDistance(totalDistance(points.slice(0, i + 1))),
      },
      geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
    })),
  };

  const total = totalDistance(points);

  return (
    <>
      <Source id="__measure_line" type="geojson" data={lineGeoJson}>
        <Layer
          id="__measure_line_layer"
          type="line"
          paint={{ 'line-color': '#ffca28', 'line-width': 2.5, 'line-dasharray': [3, 2] }}
        />
      </Source>
      <Source id="__measure_points" type="geojson" data={pointsGeoJson}>
        <Layer
          id="__measure_points_halo"
          type="circle"
          paint={{ 'circle-radius': 6, 'circle-color': '#fff' }}
        />
        <Layer
          id="__measure_points_fill"
          type="circle"
          paint={{ 'circle-radius': 4, 'circle-color': '#ffca28' }}
        />
        <Layer
          id="__measure_labels"
          type="symbol"
          filter={['>', ['get', 'index'], 0]}
          layout={{
            'text-field': ['get', 'distance'],
            'text-size': 11,
            'text-offset': [0, -1.5],
            'text-allow-overlap': true,
          }}
          paint={{
            'text-color': '#fff',
            'text-halo-color': '#000',
            'text-halo-width': 1,
          }}
        />
      </Source>

      {points.length >= 2 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            right: 10,
            zIndex: 10,
            bgcolor: 'rgba(30,30,30,0.92)',
            backdropFilter: 'blur(6px)',
            borderRadius: 1,
            p: 1,
            boxShadow: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" fontWeight={600} color="warning.main">
              {formatDistance(total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({points.length - 1} seg{points.length > 2 ? 's' : ''})
            </Typography>
            <IconButton size="small" onClick={onUndo} sx={{ p: 0.25 }}>
              <UndoIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton size="small" onClick={onClear} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default MapMeasureOverlay;
